/**
 * This class define a mobile navigation with drawer as a custom element.
 * You can affect specific direction to the menu (left | right) in the "position" element attribute.
 * You must also define an opener selector to the menu with the "open-button" attribute.
 *
 * - window.openMobileMenu Open the mobile menu
 * - window.closeMobileMenu Close the mobile menu
 */
class NavigationMobile extends HTMLElement {
    constructor() {
        super();

        this.bindEvents = this.bindEvents.bind(this);
        this.getSubmenusHeight = this.getSubmenusHeight.bind(this);
        this.initSubmenus = this.initSubmenus.bind(this);
        this.showSubmenu = this.showSubmenu.bind(this);
        this.resetSubmenus = this.resetSubmenus.bind(this);
    }

    connectedCallback() {
        this.NavigationMobile = this;
        this.Header = document.querySelector("header");
        this.Searchdrawer = document.querySelector("w3-drawer-search");
        this.NavigationDrawer = this.NavigationMobile.querySelector('w3-drawer');
        this.navigationLinklist = this.NavigationMobile.querySelector('.js-main-navigation-linklist');
        this.navigationSubLinklist = this.NavigationMobile.querySelector('.js-main-navigation-sub-linklist');
        this.navigationTitle = this.NavigationMobile.querySelector('.js-nav-title');
        this.currentSubmenus = {};

        this.isNavigationInit = false;
        this.currentMenuLevel = this.getCurrentLevel();
        this.mobileBreakpoint = 1024;
        this.isMobile = window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches;

        if (!this.isMobile) return;

        this.defineMobileDrawerPosition();

        this.bindEvents();
    }

    bindEvents() {
        this.NavigationDrawer.addEventListener('drawer--open', (e) => {

            if (this.Searchdrawer?.classList.contains("drawer--visible")) {
                this.Searchdrawer?.classList.remove("drawer--visible")
            }

            if (!this.isNavigationInit) {
                this.getSubmenusHeight();
                this.initSubmenus();
            }
        });

        this.NavigationDrawer.addEventListener('drawer--close', (e) => {
            this.resetSubmenus();
        });

        window.addEventListener("scroll", () => this.defineMobileDrawerPosition())
    }

    defineMobileDrawerPosition() {
        const headerPosition = this.Header.getBoundingClientRect().y;
        if (headerPosition > 0) {
            this.NavigationDrawer.style.transform = `translateY(${this.Header.offsetHeight + headerPosition}px)`; 
        } else {
            this.NavigationDrawer.style.transform = `translateY(${this.Header.offsetHeight - 1}px)`;
        }
    }

    initSubmenus() {
        this.navigationPrevLevel = this.NavigationDrawer?.querySelector('.js-nav-previous');
        this.navigationLinks = this.navigationLinklist?.querySelectorAll('.js-navigation-link');
        this.navigationSubLinks = this.navigationSubLinklist?.querySelectorAll('.js-navigation-link');

        this.showFirstSubmenu();

        if (this.navigationLinks.length > 0) {
            this.navigationLinks.forEach(link => {
                link.addEventListener('click', (e) => {
                    this.defineMobileDrawerPosition();
                    e.preventDefault();
        
                    const parentMenu = link.closest('.js-main-navigation-linklist');
                    const level = parentMenu?.getAttribute("data-level");
                    const subMenu = link.parentNode.querySelector(".js-main-navigation-sub-linklist");
                    const arrowIcon = link.querySelector(".icon-arrow-down");
        
                    if (level === "2" && subMenu) {
                        link.classList.toggle("no-border");
                        subMenu.classList.toggle("js-navigation-sub-link");
                        arrowIcon?.classList.toggle("scale");
                    } else {
                        this.showSubmenu(link);
                    }
                });
            });
        }

        if(this.navigationPrevLevel) {
            this.navigationPrevLevel.addEventListener('click', () => {
                const targetSubmenu = this.currentSubmenus[this.getCurrentLevel()]
                targetSubmenu.scrollTop = 0;
                this.hideSubmenu();
                this.closeSubMenus() 
            });
        }

        this.isNavigationInit = true;
    }

    closeSubMenus() {
        this.querySelectorAll(".js-navigation-sub-link").forEach(el => 
            el.classList.remove("js-navigation-sub-link")
        );

        this.querySelectorAll(".navigation-linklist .scale").forEach(el => 
            el.classList.remove("scale")
        );

        this.showFirstSubmenu();
    }

    showFirstSubmenu() {
        this.querySelectorAll(".first_sublink").forEach(el => {
            const subLink = el.querySelector(".js-main-navigation-sub-linklist");
            const svg = el.querySelector("svg");
            const link = el.querySelector("a");
            subLink?.classList.add("js-navigation-sub-link")
            svg?.classList.add("scale")
            link?.classList.add("no-border")
            }
        );

        document.querySelectorAll('a.navigation-linklist__label-bg').forEach(link => {
            const li = link.closest('li');
            const ul = li.closest('ul');
          
            if (!li || !ul) return;
          
            const listItems = Array.from(ul.children).filter(el => el.tagName === 'LI');
            const index = listItems.indexOf(li);
          
            if (index > 0) {
              const previousLi = listItems[index - 1];
              previousLi?.querySelector("a")?.classList.add("no-border")
            }
          });
    }

    showSubmenu(submenuEl) {
        const targetSubmenuTitle = submenuEl.getAttribute('title'); 
        const targetSubmenuName = submenuEl.dataset.targetName;
        const targetSubmenu = this.navigationLinklist.querySelector(`div[data-name="${targetSubmenuName}"]`);

        if (targetSubmenu) {
            this.navigationTitle.innerHTML = targetSubmenuTitle;
            this.updateCurrentLevel(1);
            targetSubmenu.classList.add('visible');
            this.currentSubmenus[this.getCurrentLevel()] = targetSubmenu;
        }
    }

    hideSubmenu() {
        const targetSubmenu = this.currentSubmenus[this.getCurrentLevel()];
        if (targetSubmenu) {
            targetSubmenu.classList.remove('visible');
            delete this.currentSubmenus[this.getCurrentLevel()];
            this.updateCurrentLevel(-1);
        }
    }

    resetSubmenus() {
        this.NavigationMobile.dataset.currentLevel = 1;
        for (const [key, submenuEl] of Object.entries(this.currentSubmenus)) {
            submenuEl.classList.remove('visible');
        }
        this.currentSubmenus = {};
    }

    getCurrentLevel() {
        return parseInt(this.NavigationMobile.dataset.currentLevel);
    }

    updateCurrentLevel(level) {
        const currentLevel = this.getCurrentLevel();
        this.NavigationMobile.dataset.currentLevel = currentLevel + level;
    }

    getSubmenusHeight() {
        const navigationDrawerHeaderHeight = this.querySelector(".drawer__header")?.offsetHeight ?? 0;
        const navigationDrawerFooterHeight = this.querySelector(".drawer__footer")?.offsetHeight ?? 0;
        let vhInPixels = 100 * window.innerHeight / 100;
        let maxHeight = vhInPixels - navigationDrawerHeaderHeight - navigationDrawerFooterHeight;
        this.NavigationMobile.style.setProperty('--nav-subitems-height', `${maxHeight}px`);
    }
}

customElements.define('w3-navigation-mobile', NavigationMobile);
